import { Box, Grid, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import {
  firstDayOfMonth,
  getCircleData,
  getDateText,
  getDayOfWeek,
  getMonthText,
  getStackedBarData,
  getYearDateList,
} from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwGraphCard } from './RwwGraphCard';
import {
  KEY_READ_TEXT,
  KEY_SCHOOL_DAILY_WALK_ACHIEVEMENT_DATA,
  KEY_SCHOOL_DAILY_WALK_ACHIEVEMENT_STAT,
  KEY_SCHOOL_DAILY_WRITE_ACHIEVEMENT_DATA,
  KEY_SCHOOL_DAILY_WRITE_ACHIEVEMENT_STAT,
  KEY_SCHOOL_MONTHLY_READ_ACHIEVEMENT_DATA,
  KEY_SCHOOL_MONTHLY_WALK_ACHIEVEMENT_DATA,
  KEY_SCHOOL_MONTHLY_WRITE_ACHIEVEMENT_DATA,
  KEY_SCHOOL_READ_ACHIEVEMENT_STAT,
  KEY_SCHOOL_WALK_ACHIEVEMENT_STAT,
  KEY_SCHOOL_WRITE_ACHIEVEMENT_STAT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  getSchoolDailyWalkAchievementData,
  getSchoolDailyWalkAchievementStat,
  getSchoolDailyWriteAchievementData,
  getSchoolDailyWriteAchievementStat,
  getSchoolMonthlyReadAchievementData,
  getSchoolMonthlyWalkAchievementData,
  getSchoolMonthlyWriteAchievementData,
  getSchoolReadAchievementStat,
  getSchoolWalkAchievementStat,
  getSchoolWriteAchievementStat,
} from '../../common/api/ApiSchool';
import { selectSchoolInfoState } from '../../common/atom';
import { useSession } from '../../hooks/session';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwAchievementStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const { loginVal } = useSession();
  const schoolClassInfo = useRecoilValue(selectSchoolInfoState);
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [readCircleData, setReadCircleData] = useState<any[]>([]);
  const [walkCircleData, setWalkCircleData] = useState<any[]>([]);
  const [writeCircleData, setWriteCircleData] = useState<any[]>([]);
  const [readStackedBarData, setReadStackedBarData] = useState<any[]>([]);
  const [walkStackedBarData, setWalkStackedBarData] = useState<any[]>([]);
  const [writeStackedBarData, setWriteStackedBarData] = useState<any[]>([]);
  const [readBarLineData, setReadBarLineData] = useState<any[]>([]);
  const [walkBarLineData, setWalkBarLineData] = useState<any[]>([]);
  const [writeBarLineData, setWriteBarLineData] = useState<any[]>([]);
  const [readMultiLineData, setReadMultiLineData] = useState<any[]>([]);
  const [walkMultiLineData, setWalkMultiLineData] = useState<any[]>([]);
  const [writeMultiLineData, setWriteMultiLineData] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [stackedBarWalkDate, setStackedBarWalkDate] = useState<Date>();
  const [stackedBarWriteDate, setStackedBarWriteDate] = useState<Date>();
  const [multiLineWalkDate, setMultiLineWalkDate] = useState<Date>();
  const [multiLineWriteDate, setMultiLineWriteDate] = useState<Date>();
  const [barLineWalkDate, setBarLineWalkDate] = useState<Date>();
  const [barLineWriteDate, setBarLineWriteDate] = useState<Date>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);

  const {
    data,
    isLoading,
    refetch: refetchSchoolReadAchievementStat,
  } = useQuery(
    KEY_SCHOOL_READ_ACHIEVEMENT_STAT,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolReadAchievementStat(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setReadCircleData(getCircleData(res.rate, false, KEY_READ_TEXT));
          setReadStackedBarData(getStackedBarData(res.rate));
        } else {
          setReadCircleData([]);
          setReadStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolWalkAchievementStat } = useQuery(
    KEY_SCHOOL_WALK_ACHIEVEMENT_STAT,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolWalkAchievementStat(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWalkCircleData(getCircleData(res.rate, false, KEY_WALK_TEXT));
        } else {
          setWalkCircleData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolWriteAchievementStat } = useQuery(
    KEY_SCHOOL_WRITE_ACHIEVEMENT_STAT,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolWriteAchievementStat(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWriteCircleData(getCircleData(res.rate, false, KEY_WRITE_TEXT));
        } else {
          setWriteCircleData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolDailyWalkAchievementStat } = useQuery(
    KEY_SCHOOL_DAILY_WALK_ACHIEVEMENT_STAT,
    () => {
      if (
        reqRequest &&
        stackedBarWalkDate !== undefined &&
        !Number.isNaN(stackedBarWalkDate?.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(stackedBarWalkDate.getTime()),
          month: getMonthText(stackedBarWalkDate.getTime()),
          day: getDateText(stackedBarWalkDate.getTime()),
        };
        return getSchoolDailyWalkAchievementStat(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWalkStackedBarData(getStackedBarData(res.rate));
        } else {
          setWalkStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolDailyWriteAchievementStat } = useQuery(
    KEY_SCHOOL_DAILY_WRITE_ACHIEVEMENT_STAT,
    () => {
      if (
        reqRequest &&
        stackedBarWriteDate !== undefined &&
        !Number.isNaN(stackedBarWriteDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(stackedBarWriteDate.getTime()),
          month: getMonthText(stackedBarWriteDate.getTime()),
          day: getDateText(stackedBarWriteDate.getTime()),
        };
        return getSchoolDailyWriteAchievementStat(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWriteStackedBarData(getStackedBarData(res.rate));
        } else {
          setWriteStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolMonthlyReadAchievementData } = useQuery(
    KEY_SCHOOL_MONTHLY_READ_ACHIEVEMENT_DATA,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolMonthlyReadAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setReadBarLineData(sortedBarValueData);
          setReadMultiLineData(sortedBarData);
        } else {
          setReadBarLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolDailyWalkAchievementData } = useQuery(
    KEY_SCHOOL_DAILY_WALK_ACHIEVEMENT_DATA,
    () => {
      if (
        reqRequest &&
        multiLineWalkDate !== undefined &&
        !Number.isNaN(multiLineWalkDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(multiLineWalkDate.getTime()),
          month: getMonthText(multiLineWalkDate.getTime()),
          day: getDateText(multiLineWalkDate.getTime()),
        };
        return getSchoolDailyWalkAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setWalkBarLineData(sortedBarValueData);
          setWalkMultiLineData(sortedBarData);
        } else {
          setWalkBarLineData([]);
          setWalkMultiLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolDailyWriteAchievementData } = useQuery(
    KEY_SCHOOL_DAILY_WRITE_ACHIEVEMENT_DATA,
    () => {
      if (
        reqRequest &&
        multiLineWriteDate !== undefined &&
        !Number.isNaN(multiLineWriteDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(multiLineWriteDate.getTime()),
          month: getMonthText(multiLineWriteDate.getTime()),
          day: getDateText(multiLineWriteDate.getTime()),
        };
        return getSchoolDailyWriteAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setWriteBarLineData(sortedBarValueData);
          setWriteMultiLineData(sortedBarData);
        } else {
          setWriteBarLineData([]);
          setWriteMultiLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const reloadData = () => {
    refetchSchoolReadAchievementStat();
    refetchSchoolWalkAchievementStat();
    refetchSchoolWriteAchievementStat();
    refetchSchoolMonthlyReadAchievementData();
  };

  const reloadDailyData = () => {
    refetchSchoolDailyWalkAchievementStat();
    refetchSchoolDailyWriteAchievementStat();
    refetchSchoolDailyWalkAchievementData();
    refetchSchoolDailyWriteAchievementData();
  };

  const refreshDailyDate = () => {
    const newDate = firstDayOfMonth(startDate * 1000);
    setStackedBarWalkDate(newDate);
    setStackedBarWriteDate(newDate);
    setMultiLineWalkDate(newDate);
    setMultiLineWriteDate(newDate);
    setBarLineWalkDate(newDate);
    setBarLineWriteDate(newDate);
  };

  useEffect(() => {
    if (reqRequest) {
      refetchSchoolDailyWalkAchievementStat();
    }
  }, [stackedBarWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchSchoolDailyWriteAchievementStat();
    }
  }, [stackedBarWriteDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchSchoolDailyWalkAchievementData();
      setBarLineWalkDate(multiLineWalkDate);
    }
  }, [multiLineWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      setMultiLineWalkDate(barLineWalkDate);
    }
  }, [barLineWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchSchoolDailyWriteAchievementData();
      setBarLineWriteDate(multiLineWriteDate);
    }
  }, [multiLineWriteDate]);

  useEffect(() => {
    if (reqRequest) {
      setMultiLineWriteDate(barLineWriteDate);
    }
  }, [barLineWriteDate]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      reloadData();
      reloadDailyData();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    refreshDailyDate();
    if (reqRequest) {
      setReqRequest(true);
      reloadData();
    }
  }, [startDate]);

  useEffect(() => {
    if (reqRequest) {
      setReqRequest(true);
      reloadData();
    }
  }, [reqRequest]);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = getYear(currentDate);
    const targetYear = loginVal.value.user.year
      ? loginVal.value.user.year
      : currentYear;
    const newDataList = getYearDateList(targetYear);

    setSelectedDate(
      loginVal.value.user.year === currentYear
        ? new Date(Date.now())
        : new Date(newDataList[0] * 1000)
    );

    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      reloadData();
      reloadDailyData();
    }
  }, []);

  return (
    <>
      <Grid container spacing={0}>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='읽기 메달 획득 비율'
            count={0}
            contents={readCircleData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='걷기 메달 획득 비율'
            count={0}
            contents={walkCircleData}
            startDate={startDate}
            offset={100}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='쓰기 메달 획득 비율'
            count={0}
            contents={writeCircleData}
            startDate={startDate}
            offset={200}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
      </Grid>
      {/* <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='읽기 메달 달성률'
            count={0}
            contents={readStackedBarData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='걷기 메달 달성률'
            count={0}
            contents={walkStackedBarData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={stackedBarWalkDate}
            setSelectedDate={setStackedBarWalkDate}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='쓰기 메달 달성률'
            count={0}
            contents={writeStackedBarData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={stackedBarWriteDate}
            setSelectedDate={setStackedBarWriteDate}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='읽기 분포도'
            count={0}
            contents={readMultiLineData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            showStat={true}
            labelTitle={['완독 권수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='걷기 분포도'
            count={0}
            contents={walkMultiLineData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={multiLineWalkDate}
            setSelectedDate={setMultiLineWalkDate}
            labelTitle={['걸음수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='쓰기 분포도'
            count={0}
            contents={writeMultiLineData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={multiLineWriteDate}
            setSelectedDate={setMultiLineWriteDate}
            labelTitle={['쓰기 횟수', '학생수']}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='읽기 분포도'
            count={0}
            contents={readBarLineData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            labelTitle={['완독 권수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='걷기 분포도'
            count={0}
            contents={walkBarLineData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={barLineWalkDate}
            setSelectedDate={setBarLineWalkDate}
            labelTitle={['걸음수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='쓰기 분포도'
            count={0}
            contents={writeBarLineData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={barLineWriteDate}
            setSelectedDate={setBarLineWriteDate}
            labelTitle={['쓰기 횟수', '학생수']}
          />
        </Grid>
      </Grid> */}
    </>
  );
};
